import type { AssistantMessagesPage } from '@agently/types';
import type { AssistantMessage } from '@agently/types';
import type { InfiniteData } from '@tanstack/react-query';
import { createInfiniteQuery } from 'react-query-kit';

import { assistantApi } from './client';
import { dedupeById } from './dedupe';

const MESSAGES_PAGE_SIZE = 50;

const useThreadMessagesPages = createInfiniteQuery({
  queryKey: ['assistant', 'threads', 'messages'],
  fetcher: async (
    { threadId }: { threadId: string },
    { pageParam }: { pageParam: unknown },
  ): Promise<AssistantMessagesPage> => {
    const { data } = await assistantApi.get<AssistantMessagesPage>(
      `/threads/${threadId}/messages`,
      { params: { limit: MESSAGES_PAGE_SIZE, before: pageParam } },
    );
    return data;
  },
  initialPageParam: undefined as string | undefined,
  // Pages arrive newest first; the next page is older than the first message.
  getNextPageParam: (lastPage) =>
    lastPage.hasMore ? lastPage.messages[0]?.id : undefined,
});

// Oldest to newest, no duplicates.
const flattenMessages = (data: InfiniteData<AssistantMessagesPage>) =>
  dedupeById<AssistantMessage>(
    [...data.pages].reverse().flatMap((page) => page.messages),
  );

export const useGetThreadMessages = Object.assign(
  (variables: { threadId: string }) =>
    useThreadMessagesPages({ variables, select: flattenMessages }),
  {
    getKey: useThreadMessagesPages.getKey,
    getFetchOptions: useThreadMessagesPages.getFetchOptions,
  },
);
